<script>
import { mapGetters } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useFocusConversationQueue } from 'dashboard/composables/useFocusConversationQueue';
import FocusQueueBar from 'dashboard/components-next/Conversation/FocusQueue/FocusQueueBar.vue';
import FocusQueueEmpty from 'dashboard/components-next/Conversation/FocusQueue/FocusQueueEmpty.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
    FocusQueueBar,
    FocusQueueEmpty,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const {
      isListHidden,
      toggleListCompact,
      startListDrag,
      startRightDrag,
      listDragWidth,
    } = useWorkbenchLayout();
    const { isQueueEmpty } = useFocusConversationQueue({ autopilot: true });

    return {
      accountId,
      windowWidth,
      isListHidden,
      isQueueEmpty,
      toggleListCompact,
      startListDrag,
      startRightDrag,
      listDragWidth,
    };
  },
  data() {
    return {
      showSearchModal: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return !this.isOnSmallScreen || !this.conversationId;
    },
    isListRailClosed() {
      return (
        this.isListHidden && !this.isOnSmallScreen && this.listDragWidth == null
      );
    },
    showMessageView() {
      return !this.isOnSmallScreen || Boolean(this.conversationId);
    },
    isOnSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },

    // SiteUp E4.1: o painel de contato agora anima o próprio colapso (ver
    // ConversationSidebar.vue) — por isso este v-if só cobre "existe
    // conversa pra mostrar", não "está aberto". Montar sempre que houver
    // conversa é o que permite a transição rodar em vez da DOM sumir na
    // hora do clique.
    hasCurrentChat() {
      return Boolean(this.currentChat.id);
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      // SiteUp: ai_agent_mode / ai_agent_active_assistant (modo da IA da caixa, que
      // alimentam o indicador + o controle Pausar/Devolver na barra) só vêm no SHOW
      // da conversa, não no serializer da lista (deixados fora p/ evitar N+1). O
      // Chatwoot só buscava o SHOW quando a conversa NÃO estava na lista — então ao
      // abrir uma conversa já listada (caso comum) o campo nunca chegava e o controle
      // de IA não renderizava. Buscamos o SHOW também quando falta o dado de IA.
      if (!chat || chat.ai_agent_mode === undefined) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section class="flex flex-col w-full h-full min-w-0">
    <FocusQueueBar v-if="isListHidden" />
    <FocusQueueEmpty v-if="isQueueEmpty" />
    <section v-show="!isQueueEmpty" class="flex w-full min-h-0 min-w-0 flex-1">
      <div
        class="workbench-list-rail h-full min-h-0"
        :class="{
          'workbench-list-rail--closed': isListRailClosed,
          'workbench-list-rail--dragging': listDragWidth != null,
        }"
      >
        <div class="workbench-list-rail__inner">
          <ChatList
            :show-conversation-list="showConversationList"
            :conversation-inbox="inboxId"
            :label="label"
            :team-id="teamId"
            :conversation-type="conversationType"
            :folders-id="foldersId"
            :is-on-expanded-layout="isOnSmallScreen"
            @conversation-load="onConversationLoad"
          />
        </div>
      </div>
      <div
        v-if="!isOnSmallScreen"
        data-testid="conversation-workbench-list-gutter"
        class="hidden md:flex relative w-2 shrink-0 h-full cursor-col-resize group items-center justify-center"
        @mousedown="startListDrag"
        @touchstart="startListDrag"
        @dblclick="toggleListCompact"
      >
        <div
          class="absolute inset-y-0 w-px bg-transparent group-hover:bg-n-brand transition-colors"
        />
      </div>
      <ConversationBox
        v-if="showMessageView"
        :inbox-id="inboxId"
        :is-on-expanded-layout="isOnSmallScreen"
      />
      <div
        v-if="!isOnSmallScreen && hasCurrentChat"
        data-testid="conversation-workbench-right-gutter"
        class="hidden md:block relative w-2 shrink-0 h-full cursor-col-resize group"
        @mousedown="startRightDrag"
        @touchstart="startRightDrag"
      >
        <div
          class="absolute inset-y-0 w-px bg-transparent group-hover:bg-n-brand transition-colors"
        />
      </div>
      <ConversationSidebar v-if="hasCurrentChat" :current-chat="currentChat" />
      <SidepanelSwitch v-if="currentChat.id" />
      <CmdBarConversationSnooze />
    </section>
  </section>
</template>

<style scoped>
/* Fecha a lista no grid (0fr), não na width do ChatList — o Virtualizer
   não recalcula item a cada frame. Mesma receita da nav no Dashboard. */
.workbench-list-rail {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  transition: grid-template-columns var(--motion-duration-layout)
    var(--motion-ease-spring);
}
.workbench-list-rail--closed {
  grid-template-columns: minmax(0, 0fr);
  pointer-events: none;
}
.workbench-list-rail--dragging {
  transition: none;
}
.workbench-list-rail__inner {
  display: flex;
  min-width: 0;
  overflow: hidden;
  height: 100%;
}
</style>
